<template>
  <v-app-bar height="64" density="compact">
    <v-app-bar-nav-icon
      color="white"
      @click="toggleState()"
    />
    <v-toolbar-items class="align-center w-100">
      <BreadCrumbs />
      <v-spacer />
      <v-btn
        v-if="authenticated"
        class="text-none"
        color="white"
        @click="logoutModalActivator = true"
      >
        {{ loggedInDisplayName }}
        <v-icon class="pl-1">account_circle</v-icon>
      </v-btn>
      <PopoverMessages
        class="infoPopover"
        :messages="infoMessages"
        color="white"
        icon="mdi:mdi-information-outline"
        location="bottom"
        hover
        iconOnly
        small
      >
        <template #additionalActions>
          <v-btn text color="primary" @click="toggleActionModal()">Change Cash Drawer</v-btn>
        </template>
      </PopoverMessages>
      <ActionModal
        v-if="showActionModal"
        :action="{ actionlabel: 'Change Cash Drawer' }"
        :showActionModal="showActionModal"
        persistent
        @toggle-modal="toggleActionModal"
      >
        <template #content>
          <v-card-text class="pb-0">
            <CashDrawer
              :enabledCashDrawers="enabledCashDrawers"
              :model="selectedCashDrawer"
              @cash-drawer-changed="updateSelectedCashDrawer"
            />
          </v-card-text>
          <v-card-actions class="pt-0">
            <v-spacer />
            <v-btn color="grey darken-1" variant="text" @click.stop="toggleActionModal">Cancel</v-btn>
            <v-btn
              color="blue darken-1"
              :disabled="!selectedCashDrawer.cashdrawerid"
              text
              @click.stop="changeCashDrawer"
            >Save</v-btn>
          </v-card-actions>
        </template>
      </ActionModal>
      <v-menu>
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            class="px-0"
            color="white"
            density="compact"
            size="small"
          >
            <v-icon
              ref="themeIcon"
              class="theme-icon"
              size="x-large"
            >
              mdi:mdi-theme-light-dark
            </v-icon>
          </v-btn>
        </template>
        <v-list>
          <template v-for="([k, v], index) in Object.entries(themeOptions)" :key="index">
            <v-list-item
              color="primary"
              :active="k === activeTheme"
              @click="setTheme(k)"
            >
              {{ v }}
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
      <v-btn
        color="white"
        class="text-none opacity-100"
        variant="text"
        disabled
      >{{ databaseName }}</v-btn>
    </v-toolbar-items>
    <LogoutModal :activator="logoutModalActivator" @toggle-activator="toggleLogoutActivator" />
  </v-app-bar>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import _get from 'lodash/get';
import { getCookie, updateCookie, getSystemTheme } from '@/lib/utils';
import useWorkstationCookie from '@/hooks/common/useWorkstationCookie';

import BreadCrumbs from '@/components/core/BreadCrumbs.vue';
import PopoverMessages from '@/components/core/common/PopoverMessages.vue';

const ActionModal = defineAsyncComponent(() => import('@/components/core/common/ActionModal.vue'));
const CashDrawer = defineAsyncComponent(() => import('@/components/login/CashDrawer.vue'));
const LogoutModal = defineAsyncComponent(() => import('@/components/core/LogoutModal.vue'));

/**
 * Vuetify toolbar comprising of menu, breadcrumbs, user profile icon, and cash drawer.
 */
export default {
  components: {
    ActionModal,
    CashDrawer,
    BreadCrumbs,
    PopoverMessages,
    LogoutModal,
  },

  data() {
    return {
      showActionModal: false,
      selectedCashDrawer: null,
      logoutModalActivator: false,
      activeTheme: null,
      themeOptions: {
        light: 'Light',
        dark: 'Dark',
        system: 'Auto',
      },
    };
  },

  setup() {
    const { updateWorkstationCookie } = useWorkstationCookie();
    return { updateWorkstationCookie };
  },

  created() {
    this.setupThemeListener();
    this.initTheme();
    this.selectedCashDrawer = this.cashDrawer;
  },

  mounted() {
    this.setThemeIconRotation();
  },

  computed: {
    infoMessages() {
      return [
        `Site: ${this.loggedInSiteId}`,
        `Cash Drawer: ${_get(this.cashDrawer, 'cashdrawerid', '')}`,
        `Version: ${process.env.VUE_APP_LOFT_VER}`,
      ];
    },

    ...mapGetters({
      authenticated: 'auth/authenticated',
      loggedInSite: 'auth/loggedInSite',
      loggedInSiteId: 'auth/loggedInSiteId',
      loggedInDisplayName: 'auth/displayname',
      enabledCashDrawers: 'auth/enabledCashDrawers',
      cashDrawer: 'auth/cashDrawer',
      databaseName: 'meta/databaseName',
    }),
  },

  methods: {
    setupThemeListener() {
      const themeQuery = window.matchMedia('(prefers-color-scheme: dark)');

      themeQuery.addEventListener('change', (e) => {
        if (getCookie('theme') !== 'system') return;
        let theme = 'light';
        if (e.matches) theme = 'dark';
        this.$vuetify.theme.change(theme);
        this.setThemeIconRotation();
      });
    },

    initTheme() {
      const theme = getCookie('theme');
      let t = theme;
      if (theme === 'system') t = getSystemTheme();
      this.$vuetify.theme.change(t);
      this.activeTheme = theme;
    },

    setTheme(theme) {
      const t = theme === 'system' ? getSystemTheme() : theme;
      this.$vuetify.theme.change(t);
      updateCookie('theme', theme, 365);
      this.activeTheme = theme;
      this.setThemeIconRotation();
    },

    /**
    /**
     * Indicates that the applications cash drawer was changed.
     */
    changeCashDrawer() {
      this.setCashDrawer(this.selectedCashDrawer);
      this.updateWorkstationCookie({ cashdrawerid: this.selectedCashDrawer.cashdrawerid, siteid: this.loggedInSiteId });
      this.toggleActionModal();
    },

    /**
     * Toggle on/off the action modal.
     */
    toggleActionModal() {
      this.showActionModal = !this.showActionModal;
    },

    /**
     * Toggles logoutModalActivator to passed boolean value.
     * @param {Boolean} bool - State to toggle logoutModalActivator to.
     */
    toggleLogoutActivator(bool) {
      this.logoutModalActivator = bool;
    },

    setThemeIconRotation() {
      this.$refs.themeIcon.$el.style.transform = this.$vuetify.theme.name === 'light' ? 'rotate(180deg)' : null;
    },

    /**
     * Update the selected cash drawer.
     * @param {Object} cashDrawer - Payload containing cash drawer id.
     */
    updateSelectedCashDrawer(cashDrawer) {
      const selectedDrawer = this.enabledCashDrawers.find(drawer => drawer.cashdrawerid === cashDrawer.value);
      this.selectedCashDrawer = selectedDrawer;
    },

    ...mapActions({
      toggleState: 'menu/toggleState',
      setCashDrawer: 'auth/setCashDrawer',
    }),
  },
};
</script>
<style lang="scss" scoped>
  .theme-icon {
    transition: transform 0.5s ease-in-out;
  }

  .v-icon {
    opacity: var(--v-high-emphasis-opacity) !important;
  }

  #topbar-hamburger, .version > span {
    color: white;
  }

  .infoPopover {
    z-index: 15;
  }
</style>
